<main class="showcase help">
  <header class="help__header">
    <div>
      <p class="sc-subheading help__eyebrow">Hyperscene</p>
      <h1 class="help__title">Viewer help</h1>
      <p class="sc-muted help__lead">
        How to move around a scene, what the toolbar does and what each tab of the viewer side panel is for.
      </p>
    </div>
    <div class="help__header-actions">
      <a class="sc-button sc-button--primary" routerLink="/viewer">Open the viewer</a>
      <a class="sc-button" routerLink="/showcase">Showcases</a>
    </div>
  </header>

  <div class="help__layout">
    <nav class="help__toc" aria-label="On this page">
      <details class="sc-card help__toc-card" [open]="tocOpen">
        <summary class="help__toc-summary">On this page</summary>
        <ol class="help__toc-list">
          <li *ngFor="let section of sections">
            <a [routerLink]="[]" [fragment]="section.id" (click)="scrollToSection(section.id)">{{ section.title }}</a>
            <ol *ngIf="section.children" class="help__toc-list help__toc-list--nested">
              <li *ngFor="let child of section.children">
                <a [routerLink]="[]" [fragment]="child.id" (click)="scrollToSection(child.id)">{{ child.title }}</a>
              </li>
            </ol>
          </li>
        </ol>
      </details>
    </nav>

    <article class="help__content">
      <section class="help__section" aria-labelledby="overview">
        <h2 id="overview">What the viewer is for</h2>
        <p>
          The viewer at <a routerLink="/viewer">/viewer</a> is the engine stand of Hyperscene. It opens a scene from the
          demo backend in the browser and streams only what the camera can see: BIM and CAD models, point clouds and
          photogrammetry, from a single building up to a city district. Use it to inspect a model, find elements and
          their attributes, measure, cut sections, save viewpoints and markers, and tune rendering and streaming.
        </p>
        <p>
          The 3D view fills the page. Around it sit the scene selector at the top, the toolbar on the right and the tab
          bar of the side panel at the bottom.
        </p>
      </section>

      <section class="help__section" aria-labelledby="first-load">
        <h2 id="first-load">Opening a scene and loading</h2>
        <ul>
          <li>
            Pick a scene in the selector at the top of the view. The address bar then carries its id
            (<code>?sceneId=…</code>), so a reload or a shared link opens the same scene.
          </li>
          <li>
            Geometry streams from the server as you move: what is close and large comes first, details follow when the
            camera stops. <em>Loading scene…</em> shows in the middle of the view until the first geometry arrives.
          </li>
          <li>
            The first open of a large scene, such as a photogrammetry district, downloads tens of megabytes and can take
            a while. The data is kept in this browser (see
            <a [routerLink]="[]" fragment="client-cache" (click)="scrollToSection('client-cache')">Client cache</a>), so
            opening the same scene again is much faster.
          </li>
        </ul>
      </section>

      <section class="help__section" aria-labelledby="desktop">
        <h2 id="desktop">Navigation with mouse and keyboard</h2>
        <h3>Mouse</h3>
        <div class="help__table-wrap">
          <table class="help__table">
            <tbody>
              <tr>
                <th scope="row">Left drag</th>
                <td>Looks around from where the camera stands, like turning your head.</td>
              </tr>
              <tr>
                <th scope="row">Right drag</th>
                <td>
                  Orbits around the model point you pressed; a dot marks the pivot while you drag. Off the model the
                  previous pivot stays.
                </td>
              </tr>
              <tr>
                <th scope="row">Middle drag</th>
                <td>Pans, keeping the pressed point under the cursor.</td>
              </tr>
              <tr>
                <th scope="row">Wheel</th>
                <td>
                  Moves toward or away from the view. With <em>Adaptive navigation (Wheel)</em> in Settings, the step
                  follows the distance to the object under the cursor.
                </td>
              </tr>
              <tr>
                <th scope="row">Click</th>
                <td>
                  Selects the object under the cursor and shows it in the Structure tab. While measuring, placing
                  markers or placing a section, the click places a point instead.
                </td>
              </tr>
              <tr>
                <th scope="row">Double-click</th>
                <td>Turns the camera to the clicked point and orbits around it from then on.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Keyboard</h3>
        <p class="sc-muted">Keys act while the 3D view has focus: click the view once first.</p>
        <div class="help__table-wrap">
          <table class="help__table">
            <tbody>
              <tr>
                <th scope="row"><kbd>W</kbd> <kbd>A</kbd> <kbd>S</kbd> <kbd>D</kbd> or arrow keys</th>
                <td>Walk forward, left, back and right. Hold the key to keep moving.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>Q</kbd> <kbd>E</kbd></th>
                <td>Move down and up.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>+</kbd> <kbd>-</kbd></th>
                <td>Raise or lower the movement speed of the keys and the wheel.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>F</kbd></th>
                <td>Zoom to the selection, or to the whole scene when nothing is selected.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>1</kbd>–<kbd>6</kbd>, <kbd>0</kbd></th>
                <td>Standard views: front, back, left, right, top, bottom and isometric.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>P</kbd></th>
                <td>Switch between perspective and parallel projection, for plans and elevations.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>X</kbd></th>
                <td>X-ray: everything but the selection turns faint.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>H</kbd> <kbd>I</kbd> <kbd>U</kbd></th>
                <td>Hide the selection, show only the selection, show everything again.</td>
              </tr>
              <tr>
                <th scope="row"><kbd>Esc</kbd></th>
                <td>
                  Steps back: stops dragging a section, cancels a section placement, clears measurement points, then
                  clears the selection.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="sc-muted">
          Shortcuts ignore keys pressed with <kbd>Ctrl</kbd>, <kbd>Alt</kbd> or <kbd>Cmd</kbd>, and keys typed into a
          field never reach the view.
        </p>
      </section>

      <section class="help__section" aria-labelledby="touch">
        <h2 id="touch">Navigation on phones and tablets</h2>
        <div class="help__table-wrap">
          <table class="help__table">
            <tbody>
              <tr>
                <th scope="row">One-finger drag</th>
                <td>Orbits around the model.</td>
              </tr>
              <tr>
                <th scope="row">Two-finger pinch</th>
                <td>Zooms toward the point between your fingers.</td>
              </tr>
              <tr>
                <th scope="row">Two-finger drag</th>
                <td>Pans; the content under your fingers follows them.</td>
              </tr>
              <tr>
                <th scope="row">Double tap</th>
                <td>Turns the camera to the tapped point.</td>
              </tr>
              <tr>
                <th scope="row"><span class="help__round-key">+</span> <span class="help__round-key">−</span></th>
                <td>Round buttons on the right edge: hold to move forward or backward.</td>
              </tr>
              <tr>
                <th scope="row"><span class="help__round-key">Fit</span></th>
                <td>Fits the whole scene into the view.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ul>
          <li>The round buttons appear on touch screens, or after the first touch on the view.</li>
          <li>
            On narrow screens the toolbar is hidden so it does not cover the model. The same commands are in the Tools
            tab.
          </li>
          <li>To select an element on a touch screen, tap its name in the Structure tab.</li>
          <li>Tap the active tab again to close its panel and free the view.</li>
        </ul>
      </section>

      <section class="help__section" aria-labelledby="toolbar">
        <h2 id="toolbar">The toolbar</h2>
        <p>
          The column of icon buttons on the right of the view holds everyday commands. Hover over a button, or focus it
          with the keyboard, to see its name and shortcut.
        </p>
        <dl class="help__defs">
          <dt>Camera</dt>
          <dd>
            <strong>Home view</strong> returns to the camera the scene opened with. <strong>Fit all</strong> zooms to
            the whole scene without turning the camera. <strong>Zoom to the selection</strong> (<kbd>F</kbd>) frames the
            selected element. <strong>Standard views</strong> opens a menu of front, back, left, right, top, bottom and
            isometric views. <strong>Parallel projection</strong> (<kbd>P</kbd>) toggles an orthographic camera.
          </dd>
          <dt>Tools</dt>
          <dd>
            <strong>Measure</strong> turns measuring on; pick the mode in the Tools tab. <strong>Section</strong> cuts
            the model with a plane. <strong>Section box</strong> keeps only what is inside a box around the selection,
            or around the whole scene when nothing is selected. While a section is on, a drag button appears: with it, a
            drag on the view moves the plane or resizes a face of the box. <strong>X-ray</strong> (<kbd>X</kbd>) fades
            everything but the selection.
          </dd>
          <dt>Visibility</dt>
          <dd>
            <strong>Hide the selection</strong> (<kbd>H</kbd>), <strong>Show only the selection</strong> (<kbd>I</kbd>)
            and <strong>Show everything</strong> (<kbd>U</kbd>).
          </dd>
          <dt>Help</dt>
          <dd>
            Opens this page in a new browser tab, so the scene stays open. On phones, where the toolbar is hidden, the
            <strong>?</strong> button next to the scene selector does the same.
          </dd>
        </dl>
      </section>

      <section class="help__section" aria-labelledby="tabs">
        <h2 id="tabs">Side panel tabs</h2>
        <p>
          The tab bar at the bottom opens one panel at a time; click the active tab again to close it. The tabs appear
          once a scene is open.
        </p>

        <section class="sc-card help__tab" aria-labelledby="tab-structure">
          <h3 id="tab-structure">Structure</h3>
          <p>The scene tree and the properties of the selected element. Use it to browse a model by its hierarchy.</p>
          <dl class="help__defs">
            <dt>Scene tree</dt>
            <dd>
              <strong>+</strong> and <strong>-</strong> expand and collapse a node; a dot marks a leaf. Click a name to
              select the element in 3D and load its properties. <code>L2 / 140</code> next to a name is the tree level
              and the number of elements below the node. The eye button hides or shows the element with its children; a
              crossed eye means hidden or partly hidden. The target button flies the camera to the element.
            </dd>
            <dt>Node properties</dt>
            <dd>
              The selected element, its visibility and its attributes as <em>group / name: value unit</em>, for example
              IFC property sets.
            </dd>
            <dt>Enterprise metadata linking</dt>
            <dd>
              Finds elements by an attribute. Enter an <strong>Attribute name</strong> such as <code>IFC_GUID</code>,
              <code>System</code> or <code>Zone</code>, press <strong>Load Values</strong> to get suggestions, type part
              of an <strong>Attribute value</strong> and press <strong>Search Nodes</strong>. Pick a match in the list
              and press <strong>Open Matched Node</strong> to select it. Web links found in the attributes of the
              selected element are listed below and open in a new tab.
            </dd>
          </dl>
        </section>

        <section class="sc-card help__tab" aria-labelledby="tab-viewpoints">
          <h3 id="tab-viewpoints">Viewpoints</h3>
          <p>
            Named camera positions stored with the scene on the backend, so everyone who opens the scene sees them. Use
            them to come back to a detail or to point a colleague at it.
          </p>
          <dl class="help__defs">
            <dt>Select viewpoint</dt>
            <dd>Moves the camera to the chosen viewpoint.</dd>
            <dt>Save Viewpoint</dt>
            <dd>Saves the current camera under the name typed in <strong>New viewpoint name</strong>.</dd>
            <dt>Refresh, Update, Rename, Delete</dt>
            <dd>
              Refresh moves the camera back to the selected viewpoint. Update overwrites it with the current camera
              after a confirmation. Rename and Delete change or remove it.
            </dd>
            <dt>Copy Viewpoint URL</dt>
            <dd>Copies a link that opens this scene at the selected viewpoint.</dd>
          </dl>
        </section>

        <section class="sc-card help__tab" aria-labelledby="tab-quality">
          <h3 id="tab-quality">Quality</h3>
          <p>How the scene looks. Choose a preset first, then fine-tune it. Hover over a field to see what it does.</p>
          <dl class="help__defs">
            <dt>Quality profile</dt>
            <dd>
              Presets from <em>Baseline</em> (flat unlit rendering, fastest) through <em>Technical</em>,
              <em>Presentation</em> and <em>High quality</em> to the <em>Enhanced</em> and <em>Studio</em> looks with
              ambient occlusion, outlines and tonemapping. Heavier looks cost more GPU time; on a weak device stay with
              Baseline or Technical.
            </dd>
            <dt>Anti-aliasing</dt>
            <dd>Off, or MSAA 2x, 4x or 8x to smooth jagged edges at a GPU cost.</dd>
            <dt>Mesh shading, Specular highlights, Fresnel silhouette</dt>
            <dd>
              Flat (unlit) or diffuse shading from surface normals, glossy highlights and a light rim that separates
              shapes from the background.
            </dd>
            <dt>Enhanced and Studio effects</dt>
            <dd>
              Shown for those profiles: ambient occlusion, crease and silhouette edges, filmic tonemapping, gradient
              background and backdrop vignette, with their strength, radius, edge width, saturation, warmth, key light
              and sharpening.
            </dd>
            <dt>Light and tone</dt>
            <dd>Ambient, directional, specular and Fresnel intensity, exposure in stops and contrast.</dd>
          </dl>
        </section>

        <section class="sc-card help__tab" aria-labelledby="tab-settings">
          <h3 id="tab-settings">Settings</h3>
          <p>
            Rendering and streaming parameters of the engine. The defaults suit most scenes; change them to trade detail
            against speed, or to investigate a problem. Hover over a field to see its exact meaning.
          </p>
          <dl class="help__defs">
            <dt>Debug switches</dt>
            <dd>
              <strong>Show request loading</strong> shows the request status card.
              <strong>Enable profiling</strong> collects render timings shown in the Scene debug tab;
              <strong>Deep GPU profiling</strong> adds GPU timer queries where the browser supports them.
            </dd>
            <dt>Render pipeline</dt>
            <dd>
              <strong>Distributed rendering</strong> draws a heavy scene over several frames to stay responsive.
              <strong>Downsampling</strong> renders at a lower resolution to relieve the GPU.
              <strong>Object sort strategy</strong> sets which objects are drawn first.
              <strong>Hide objects below size during navigation</strong> skips tiny objects while the camera moves and
              restores them when it stops. Mesh and point-cloud formats, download batch sizes, frame and work budgets,
              transparency, mesh batching (instancing), point size and adaptive point size tune how geometry arrives and
              is drawn.
            </dd>
            <dt>Camera</dt>
            <dd>
              <strong>Adaptive navigation</strong> for the wheel and the keyboard scales the speed by the distance to
              the object under the cursor. <strong>Far plane</strong> limits the drawing distance.
            </dd>
            <dt>Frustum</dt>
            <dd>
              How much the server sends for the current view: mesh and line limits, the point-cloud budget, minimum
              apparent sizes, the HLOD screen error for photogrammetry (lower is sharper and loads more), the
              photogrammetry tile memory cache and the request debounce. <strong>Fix frustum</strong> freezes server
              updates to inspect what was loaded. The box switches draw the bounds of point-cloud blocks and objects.
            </dd>
            <dt>Client cache</dt>
            <dd>
              Keeps downloaded data in this browser; see
              <a [routerLink]="[]" fragment="client-cache" (click)="scrollToSection('client-cache')">Client cache</a>
              below.
            </dd>
          </dl>
        </section>

        <section class="sc-card help__tab" aria-labelledby="tab-scene-debug">
          <h3 id="tab-scene-debug">Scene debug (FPS)</h3>
          <p>
            The tab button shows the current frame rate, for example <em>FPS 60</em>. Its panel lists live engine
            statistics: queued and visible objects, culling time, camera position, loaded meshes, triangles and points,
            cache usage against its budget, draw calls, instanced batches, shader builds and the last error.
          </p>
          <dl class="help__defs">
            <dt>Copy, Copy as JSON</dt>
            <dd>Copy the statistics to the clipboard. Attach them when you report a rendering or loading problem.</dd>
            <dt>Profiler</dt>
            <dd>
              With profiling on in Settings: frame and long-task counts, per-phase timings, counters and the latest
              frustum requests. <strong>Reset profiler</strong> starts a new measurement.
            </dd>
            <dt>Record camera path</dt>
            <dd>Records your camera movement and downloads it as a path file for the benchmark harness.</dd>
          </dl>
        </section>

        <section class="sc-card help__tab" aria-labelledby="tab-tools">
          <h3 id="tab-tools">Tools</h3>
          <p>The working tools, in collapsible groups. The toolbar buttons open the same tools.</p>
          <dl class="help__defs">
            <dt>Navigation</dt>
            <dd>
              Home, Fit all, Zoom to selection, Parallel projection, Fullscreen and the standard views.
              <strong>Use current camera as scene start</strong> makes the current camera the one the scene opens with,
              unless a link sets another; <strong>Clear scene start camera</strong> removes it.
            </dd>
            <dt>Selection &amp; visibility</dt>
            <dd>
              Hide, Isolate, Unhide selected, Show all, X-ray and Clear selection. Hiding applies to this browser only,
              in a <em>visibility session</em>; <strong>New visibility session</strong> shows everything you hid again.
              <strong>Remove selected root</strong> takes a whole model out of the scene;
              <strong>Reload scene tree</strong> reads the tree again.
            </dd>
            <dt>Section</dt>
            <dd>
              <strong>Mode</strong>: off, a plane, or a box. For a plane, choose the axis it cuts across and move it
              with the slider; for a box, move each face with its slider. <strong>Cut at a clicked point</strong> and
              <strong>Cut along a clicked face</strong> place the plane where you click next.
              <strong>Flip side</strong> keeps the other half. <strong>Box around selection</strong> keeps only what is
              inside; nothing outside is downloaded.
            </dd>
            <dt>Appearance</dt>
            <dd><strong>Global opacity</strong> makes the whole scene translucent.</dd>
            <dt>Measurement</dt>
            <dd>
              Turn it on, choose a <strong>Mode</strong> (distance, angle, polyline, area or point coordinates), a
              <strong>Snap</strong> target (surface, the selected node or the level of the first point) and a
              <strong>Unit</strong>, then click points on the model.
              <strong>Snap to corners, edge middles and edges</strong> helps with exact points. A distance, an angle or
              a point is saved as soon as it has its points; press <strong>Save</strong> to finish a polyline or an
              area. Saved measurements can be shown again from the list. <strong>Model scale</strong>: photogrammetry
              keeps the units of its source, so measure a known length, such as a door, enter its real length and press
              <strong>Calibrate</strong>.
            </dd>
            <dt>Annotations</dt>
            <dd>
              Turn on <strong>Marker placement mode</strong> and click the model to pin a named marker; focus, rename
              and delete markers from the list. <strong>Viewer Data Transfer</strong> exports and imports markers and
              viewpoints as JSON (replace or merge by id), as a file or through the clipboard, and saves them to or
              loads them from the backend scene.
            </dd>
          </dl>
        </section>

        <p class="sc-muted">
          Quality and Settings apply changes on their own: visual switches at once, typed values after a short pause,
          and settings that reload the scene after a countdown you can <strong>Cancel</strong> or skip with
          <strong>Apply</strong>. <strong>Reset to Defaults</strong> restores the defaults, and
          <strong>Copy URL with camera</strong> copies a link that reopens the scene with the current camera and
          settings.
        </p>
      </section>

      <section class="help__section" aria-labelledby="client-cache">
        <h2 id="client-cache">Client cache</h2>
        <p>
          The viewer keeps downloaded photogrammetry tiles and scene geometry in this browser's storage (IndexedDB): up
          to 3 GiB on a desktop and 1 GiB on a phone, at most half of the storage the browser allows. Reopening a scene,
          or a showcase built on it, then downloads only what changed. Point clouds are never kept.
        </p>
        <p>
          The <strong>Client cache</strong> block at the end of the Settings tab has a switch for each kind of data and
          shows how much is stored. <strong>Clear caches</strong> empties both, for example to measure a cold first load
          or to free disk space; the open scene fills them again as it loads. <strong>Refresh</strong> updates the usage
          figures.
        </p>
      </section>

      <section class="help__section" aria-labelledby="showcases">
        <h2 id="showcases">Showcases</h2>
        <p>
          The <a routerLink="/showcase">showcase section</a> shows applied scenarios built on the same engine, such as
          an urban digital twin, clash coordination and an open-pit survey. Each runs on a real scene of the demo
          backend with sample business data and walks through its story step by step.
        </p>
        <p>
          <a class="sc-button sc-button--primary" routerLink="/showcase">Browse the showcases</a>
        </p>
      </section>
    </article>
  </div>
</main>
